<template>
  <div class="container-fluid mt-4" :class="themeClass">

    <!-- ══════════════════════════════════════════════════════════
         PANEL: Cargando estado de sesión
    ═══════════════════════════════════════════════════════════ -->
    <div v-if="cargandoSesion" class="text-center py-5">
      <div class="spinner-border" style="color: var(--primary-main)" role="status">
        <span class="visually-hidden">Verificando...</span>
      </div>
      <p class="mt-3" style="color: var(--text-secondary)">Verificando estado de caja...</p>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         PANEL: Sin sesión activa → seleccionar caja y abrir
    ═══════════════════════════════════════════════════════════ -->
    <div v-if="!sesionActiva && !cargandoSesion">
      <div class="card shadow-sm mb-4">
        <div class="card-header">
          <h5 class="mb-0">
            <i class="bi bi-cash-register me-2"></i>
            Apertura de Caja
          </h5>
        </div>
        <div class="card-body">
          <div v-if="errorCarga" class="alert alert-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>{{ errorCarga }}
          </div>

          <div v-if="cargandoCajas" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
            <p class="mt-2 text-muted">Cargando cajas disponibles...</p>
          </div>

          <div v-else>
            <div v-if="cajas.length === 0" class="alert alert-warning">
              <i class="bi bi-info-circle me-2"></i>
              No hay cajas configuradas. Ingresa a
              <router-link to="/cajas/configuracion">Configuración de Cajas</router-link>
              para crear una.
            </div>

            <form v-else @submit.prevent="abrirCaja">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Seleccionar Caja <span class="text-danger">*</span></label>
                  <select v-model="formApertura.caja_id" class="form-select" required>
                    <option value="">— seleccione —</option>
                    <option
                      v-for="caja in cajasDisponibles"
                      :key="caja.id"
                      :value="caja.id"
                    >{{ caja.nombre_caja }} ({{ caja.tipo }})</option>
                  </select>
                  <div v-if="cajasOcupadas.length > 0" class="form-text text-warning">
                    <i class="bi bi-lock me-1"></i>
                    {{cajasOcupadas.map(c=>c.nombre_caja).join(', ')}} — en uso por otro operador
                  </div>
                </div>

                <div class="col-md-6">
                  <label class="form-label fw-semibold">Saldo de Apertura ($)</label>
                  <input
                    v-model.number="formApertura.saldo_apertura"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                    placeholder="0.00"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label">Observación de apertura</label>
                  <textarea
                    v-model="formApertura.observacion"
                    class="form-control"
                    rows="2"
                    placeholder="Opcional..."
                  ></textarea>
                </div>

                <div class="col-12">
                  <button type="submit" class="btn btn-success px-4" :disabled="abriendo">
                    <span v-if="abriendo" class="spinner-border spinner-border-sm me-2"></span>
                    <i v-else class="bi bi-unlock me-2"></i>
                    {{ abriendo ? 'Abriendo...' : 'Abrir Caja' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         PANEL: Sesión activa → panel de operaciones
    ═══════════════════════════════════════════════════════════ -->
    <div v-if="sesionActiva">

      <!-- Encabezado de sesión -->
      <div class="card shadow-sm mb-3 border-success">
        <div class="card-body py-3">
          <div class="row align-items-center">
            <div class="col-md-8">
              <div class="d-flex align-items-center gap-3 flex-wrap">
                <span class="badge bg-success fs-6">
                  <i class="bi bi-circle-fill me-1" style="font-size:0.5rem"></i>
                  CAJA ABIERTA
                </span>
                <span class="fw-bold fs-5">{{ sesionActiva.nombre_caja }}</span>
                <span class="text-muted small">
                  Apertura: {{ formatearFecha(sesionActiva.fecha_apertura) }}
                </span>
              </div>
            </div>
            <div class="col-md-4 text-md-end mt-2 mt-md-0 d-flex flex-wrap gap-2 justify-content-md-end">
              <button class="btn btn-outline-primary btn-sm" @click="abrirModalDeposito">
                <i class="bi bi-bank me-1"></i> Depositar a Banco
              </button>
              <button class="btn btn-outline-danger btn-sm" @click="mostrarModalCierre = true">
                <i class="bi bi-door-closed me-1"></i> Cerrar Caja
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Resumen de saldos -->
      <div class="d-flex justify-content-between align-items-center mb-2" v-if="sesionActiva.resumen">
        <span class="small text-muted">Resumen de la sesión</span>
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="recargarSesion"
          :disabled="actualizandoSaldo"
          title="Actualizar saldo"
        >
          <span v-if="actualizandoSaldo" class="spinner-border spinner-border-sm"></span>
          <i v-else class="bi bi-arrow-clockwise"></i>
          Actualizar
        </button>
      </div>
      <div class="row g-3 mb-4" v-if="sesionActiva.resumen">
        <div class="col-6 col-md-3">
          <div class="card text-center h-100 border-0 shadow-sm">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">Saldo Apertura</div>
              <div class="fs-4 fw-bold text-secondary">${{ fmt(sesionActiva.saldo_apertura) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="card text-center h-100 border-0 shadow-sm">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">Ingresos</div>
              <div class="fs-4 fw-bold text-success">+${{ fmt(sesionActiva.resumen.total_ingresos) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="card text-center h-100 border-0 shadow-sm">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">Egresos</div>
              <div class="fs-4 fw-bold text-danger">-${{ fmt(sesionActiva.resumen.total_egresos) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="card text-center h-100 border-0 shadow-sm bg-light">
            <div class="card-body py-3">
              <div class="text-muted small mb-1">Saldo Actual</div>
              <div class="fs-4 fw-bold text-primary">${{ fmt(parseFloat(sesionActiva.saldo_apertura || 0) + parseFloat((sesionActiva.resumen || {}).saldo_actual || 0)) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Últimos movimientos de la sesión -->
      <div class="card shadow-sm" v-if="ultimosMovimientos.length > 0">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0"><i class="bi bi-clock-history me-2"></i>Últimos Movimientos</h6>
          <router-link :to="'/cajas/sesiones/' + sesionActiva.id" class="btn btn-sm btn-outline-primary">
            Ver todos
          </router-link>
        </div>
        <div class="table-responsive">
          <table class="table table-sm mb-0">
            <thead class="table-light">
              <tr>
                <th>Tipo</th>
                <th>Origen</th>
                <th>Concepto</th>
                <th class="text-end">Monto</th>
                <th>Hora</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mov in ultimosMovimientos" :key="mov.id">
                <td>
                  <span :class="mov.tipo === 'ingreso' ? 'badge bg-success' : 'badge bg-danger'">
                    {{ mov.tipo }}
                  </span>
                </td>
                <td><span class="badge bg-secondary">{{ mov.origen }}</span></td>
                <td>{{ mov.concepto || mov.descripcion || '-' }}</td>
                <td class="text-end fw-semibold" :class="mov.tipo === 'ingreso' ? 'text-success' : 'text-danger'">
                  {{ mov.tipo === 'ingreso' ? '+' : '-' }}${{ fmt(mov.monto) }}
                </td>
                <td class="text-muted small">{{ formatearHora(mov.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div><!-- /sesionActiva -->

    <!-- Cargando sesión inicial -->
    <div v-if="cargandoSesion" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-2 text-muted">Verificando sesión de caja...</p>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         MODAL: Cierre de caja
    ═══════════════════════════════════════════════════════════ -->
    <div v-if="mostrarModalCierre" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-door-closed me-2"></i>Cierre de Caja</h5>
            <button type="button" class="btn-close" @click="mostrarModalCierre = false"></button>
          </div>
          <div class="modal-body" v-if="sesionActiva && sesionActiva.resumen">
            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <div class="card bg-light text-center">
                  <div class="card-body py-2">
                    <div class="small text-muted">Saldo Apertura</div>
                    <div class="fw-bold">${{ fmt(sesionActiva.saldo_apertura) }}</div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-success bg-opacity-10 text-center">
                  <div class="card-body py-2">
                    <div class="small text-muted">Total Ingresos</div>
                    <div class="fw-bold text-success">+${{ fmt(sesionActiva.resumen.total_ingresos) }}</div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-danger bg-opacity-10 text-center">
                  <div class="card-body py-2">
                    <div class="small text-muted">Total Egresos</div>
                    <div class="fw-bold text-danger">-${{ fmt(sesionActiva.resumen.total_egresos) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="alert alert-info d-flex justify-content-between align-items-center">
              <span>Saldo esperado en caja:</span>
              <strong class="fs-5">${{ fmt(saldoEsperadoCierre) }}</strong>
            </div>

            <div v-if="sesionActiva.resumen.total_creditos > 0" class="alert alert-warning">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Hay <strong>{{ sesionActiva.resumen.total_creditos }}</strong> crédito(s) pendiente(s) por
              <strong>${{ fmt(sesionActiva.resumen.saldo_creditos) }}</strong>.
              Los créditos quedarán registrados y podrán abonarse posteriormente.
            </div>

            <div class="mb-3">
              <label class="form-label fw-semibold">Conteo físico de efectivo ($)</label>
              <input
                v-model.number="formCierre.saldo_real"
                type="number"
                min="0"
                step="0.01"
                class="form-control"
                :placeholder="saldoEsperadoCierre"
              />
              <div v-if="formCierre.saldo_real !== null" class="mt-1">
                <span :class="diferenciaCierre >= 0 ? 'text-success' : 'text-danger'" class="fw-semibold">
                  Diferencia: {{ diferenciaCierre >= 0 ? '+' : '' }}${{ fmt(Math.abs(diferenciaCierre)) }}
                  ({{ diferenciaCierre >= 0 ? 'sobrante' : 'faltante' }})
                </span>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Observaciones de cierre</label>
              <textarea v-model="formCierre.observacion" class="form-control" rows="2" placeholder="Opcional..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="mostrarModalCierre = false">Cancelar</button>
            <button class="btn btn-danger" @click="cerrarCaja" :disabled="cerrando">
              <span v-if="cerrando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-door-closed me-1"></i>
              {{ cerrando ? 'Cerrando...' : 'Confirmar Cierre' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- ══════════════════════════════════════════════════════════
       MODAL: Registrar depósito bancario (caja → banco)
  ═══════════════════════════════════════════════════════════ -->
  <div v-if="modalDeposito.visible" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title"><i class="bi bi-bank me-2"></i>Depositar Efectivo a Banco</h5>
          <button type="button" class="btn-close btn-close-white" @click="modalDeposito.visible = false"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-info py-2 small mb-3">
            El monto saldrá de la caja activa y se registrará como ingreso en el banco seleccionado.
          </div>
          <div class="alert alert-danger py-2 small" v-if="modalDeposito.error">{{ modalDeposito.error }}</div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Banco destino</label>
            <select v-model="formDeposito.banco_id" class="form-select form-select-sm">
              <option value="">-- Seleccionar banco --</option>
              <option v-for="b in bancos" :key="b.id" :value="b.id">{{ b.nombre_banco }} — {{ b.numero_cuenta }}</option>
            </select>
            <div v-if="bancos.length === 0" class="form-text text-warning">
              No hay bancos configurados. <router-link to="/cajas/configuracion">Agregar banco</router-link>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Monto a depositar</label>
            <input
              v-model.number="formDeposito.monto"
              type="number"
              step="0.01" min="0.01"
              class="form-control form-control-sm"
            />
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">N.° comprobante bancario</label>
            <input
              v-model="formDeposito.numero_comprobante"
              type="text"
              class="form-control form-control-sm"
              placeholder="Opcional"
            />
          </div>

          <div class="mb-1">
            <label class="form-label fw-semibold">Descripción</label>
            <input
              v-model="formDeposito.descripcion"
              type="text"
              class="form-control form-control-sm"
              placeholder="Depósito bancario"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="modalDeposito.visible = false">Cancelar</button>
          <button
            class="btn btn-primary"
            @click="registrarDeposito"
            :disabled="modalDeposito.guardando"
          >
            <span v-if="modalDeposito.guardando" class="spinner-border spinner-border-sm me-1"></span>
            {{ modalDeposito.guardando ? 'Guardando...' : 'Registrar depósito' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import notificationService from '@/services/notificationService'

export default {
  name: 'ClientCajas',

  data() {
    return {
      // Estado general
      cargandoSesion: true,
      cargandoCajas:  false,
      errorCarga:     null,
      sesionActiva:   null,
      cajas:          [],
      bancos:         [],
      ultimosMovimientos: [],

      // Formulario apertura
      abriendo:       false,
      formApertura: {
        caja_id:        '',
        saldo_apertura: 0,
        observacion:    '',
      },

      // Modal cierre
      mostrarModalCierre: false,
      cerrando:           false,
      formCierre: {
        saldo_real:   null,
        observacion:  '',
      },

      // Modal depósito bancario
      modalDeposito: { visible: false, error: '', guardando: false },
      formDeposito: {
        banco_id:           '',
        monto:              0,
        numero_comprobante: '',
        descripcion:        'Depósito bancario',
      },

      // Auto-refresh
      actualizandoSaldo:   false,
      timerActualizacion:  null,
    }
  },

  computed: {
    themeClass() { return themeManager.themeClass },

    cajasDisponibles() {
      return this.cajas.filter(c => !c.sesion_activa && c.activo == 1)
    },
    cajasOcupadas() {
      return this.cajas.filter(c => c.sesion_activa)
    },
    saldoEsperadoCierre() {
      if (!this.sesionActiva) return 0
      const r = this.sesionActiva.resumen || {}
      return parseFloat(this.sesionActiva.saldo_apertura || 0) +
             parseFloat(r.total_ingresos || 0) -
             parseFloat(r.total_egresos  || 0)
    },
    diferenciaCierre() {
      if (this.formCierre.saldo_real === null) return 0
      return parseFloat(this.formCierre.saldo_real) - this.saldoEsperadoCierre
    },
  },

  async created() {
    await this.verificarSesionActiva()
    this.cargarBancos()
    if (this.sesionActiva) {
      this.timerActualizacion = setInterval(() => this.recargarSesion(), 45000)
    }
  },

  beforeUnmount() {
    if (this.timerActualizacion) clearInterval(this.timerActualizacion)
  },

  methods: {
    async verificarSesionActiva() {
      this.cargandoSesion = true
      try {
        const { data } = await api.get('/cajas/sesion-activa')
        this.sesionActiva = data.sesion
        if (this.sesionActiva) {
          await this.cargarUltimosMovimientos()
        } else {
          await this.cargarCajas()
        }
      } catch (e) {
        this.errorCarga = 'Error al verificar sesión de caja'
      } finally {
        this.cargandoSesion = false
      }
    },

    // Recarga silenciosa del resumen y movimientos sin spinner de pantalla completa
    async recargarSesion() {
      if (!this.sesionActiva) return
      this.actualizandoSaldo = true
      try {
        const { data } = await api.get('/cajas/sesion-activa')
        if (data.sesion) {
          this.sesionActiva = data.sesion
          await this.cargarUltimosMovimientos()
        }
      } catch { /* silencioso */ } finally {
        this.actualizandoSaldo = false
      }
    },

    async cargarCajas() {
      this.cargandoCajas = true
      try {
        const { data } = await api.get('/cajas')
        this.cajas = data.cajas || []
      } catch {
        this.errorCarga = 'Error al cargar cajas disponibles'
      } finally {
        this.cargandoCajas = false
      }
    },

    async cargarUltimosMovimientos() {
      if (!this.sesionActiva) return
      try {
        const { data } = await api.get(`/cajas/sesiones/${this.sesionActiva.id}/movimientos`)
        this.ultimosMovimientos = (data.movimientos || []).slice(0, 10)
      } catch { /* silencioso */ }
    },

    async abrirCaja() {
      if (!this.formApertura.caja_id) return
      this.abriendo = true
      try {
        await api.post(`/cajas/${this.formApertura.caja_id}/abrir`, {
          saldo_apertura: this.formApertura.saldo_apertura,
          observacion:    this.formApertura.observacion,
        })
        notificationService.success('Caja abierta correctamente')
        await this.verificarSesionActiva()
        // Iniciar auto-refresh tras abrir la caja
        if (this.sesionActiva && !this.timerActualizacion) {
          this.timerActualizacion = setInterval(() => this.recargarSesion(), 45000)
        }
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al abrir la caja'
        notificationService.error(msg)
      } finally {
        this.abriendo = false
      }
    },

    async cerrarCaja() {
      this.cerrando = true
      try {
        const { data } = await api.post(`/cajas/sesiones/${this.sesionActiva.id}/cerrar`, {
          saldo_real:  this.formCierre.saldo_real ?? this.saldoEsperadoCierre,
          observacion: this.formCierre.observacion,
        })
        notificationService.success(
          `Caja cerrada. Saldo esperado: $${this.fmt(data.saldo_esperado)} | Real: $${this.fmt(data.saldo_real)}`
        )
        this.mostrarModalCierre = false
        this.sesionActiva       = null
        this.formCierre         = { saldo_real: null, observacion: '' }
        await this.cargarCajas()
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al cerrar la caja'
        notificationService.error(msg)
      } finally {
        this.cerrando = false
      }
    },

    fmt(val) {
      return parseFloat(val || 0).toFixed(2)
    },

    formatearFecha(fecha) {
      if (!fecha) return '-'
      return new Date(fecha).toLocaleString('es-EC', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },

    formatearHora(fecha) {
      if (!fecha) return '-'
      return new Date(fecha).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
    },

    async cargarBancos() {
      try {
        const { data } = await api.get('/bancos')
        this.bancos = data.bancos || []
      } catch { /* silencioso */ }
    },

    abrirModalDeposito() {
      this.modalDeposito = { visible: true, error: '', guardando: false }
      this.formDeposito  = {
        banco_id:           this.bancos[0]?.id ?? '',
        monto:              0,
        numero_comprobante: '',
        descripcion:        'Depósito bancario',
      }
    },

    async registrarDeposito() {
      const fd = this.formDeposito
      if (!fd.banco_id)        { this.modalDeposito.error = 'Seleccione un banco destino.'; return }
      if (!fd.monto || fd.monto <= 0) { this.modalDeposito.error = 'El monto debe ser mayor a cero.'; return }
      if (!this.sesionActiva?.id) { this.modalDeposito.error = 'No hay sesión de caja activa.'; return }

      this.modalDeposito.error     = ''
      this.modalDeposito.guardando = true
      try {
        await api.post('/cajas/deposito', {
          sesion_id:          this.sesionActiva.id,
          banco_id:           fd.banco_id,
          monto:              fd.monto,
          descripcion:        fd.descripcion || 'Depósito bancario',
          numero_comprobante: fd.numero_comprobante || undefined,
        })
        notificationService.success('Depósito registrado correctamente')
        this.modalDeposito.visible = false
        await this.recargarSesion()
      } catch (e) {
        this.modalDeposito.error = e?.response?.data?.error || 'Error al registrar el depósito.'
      } finally {
        this.modalDeposito.guardando = false
      }
    },
  },
}
</script>
